/**
 * Invite flow modal: sizing and color palette.
 * Use only these values for the invite modal (ScreeningDetailPage and any shared flow).
 *
 * Palette — modern, vibrant, premium:
 * - Electric Indigo  #4F46E5
 * - Deep Navy        #0F172A
 * - Slate 700        #334155
 * - Emerald 500      #10B981
 * - Rose 500         #F43F5E
 * - Sky 500          #0EA5E9
 * - White            #FFFFFF
 * - Slate 50         #F8FAFC
 */

// --- Sizing (desktop ≥1280px, tablet 768–1279px, below 768px) ---
export const INVITE_MODAL_SIZING = {
  // Desktop (≥1280px)
  desktop: {
    maxWidth: 900,
    minWidth: 720,
    maxHeightVh: 80,
    maxHeightExtraPx: 10,
    marginVertical: 40,
    maxHeightCalc: 'calc(100vh - 80px)',
  },
  // Tablet / smaller desktop (768px–1279px)
  tablet: {
    maxWidth: 720,
    minWidth: 600,
    paddingHorizontal: 32,
    maxHeightVh: 85,
    maxHeightExtraPx: 10,
  },
  // Below 768px (must not break)
  mobile: {
    paddingHorizontal: 16,
    maxHeightVh: 100,
  },
  // Shell structure (all breakpoints)
  shell: {
    headerHeightPx: 64,
    footerHeightPx: 72,
    bodyPaddingX: 24,
    bodyPaddingY: 20,
    borderRadius: 20,
  },
};

// Breakpoints (match Tailwind: sm 640, md 768, lg 1024, xl 1280)
export const INVITE_MODAL_BREAKPOINTS = {
  tablet: 768,
  desktop: 1280,
};

// --- Color palette (hex only) ---
export const INVITE_PALETTE = {
  electricIndigo: '#4F46E5',
  deepNavy: '#0F172A',
  slate700: '#334155',
  emerald500: '#10B981',
  sky500: '#0EA5E9',
  rose500: '#F43F5E',
  white: '#FFFFFF',
  slate50: '#F8FAFC',
  // Kept for backward compat
  electricBlue: '#007BFF',
  prussianBlue: '#152236',
  shadowGrey: '#242124',
  lavenderPurple: '#8472B3',
  whiteSmoke: '#F5F5F5',
};

// --- Semantic color map (all values from palette or derived opacity) ---
export const INVITE_MODAL_COLORS = {
  // Backgrounds
  overlay: 'rgba(15, 23, 42, 0.60)',
  modalBg: '#FFFFFF',
  panelBorder: 'rgba(51, 65, 85, 0.10)',
  panelBg: 'rgba(248, 250, 252, 0.80)',

  // Text
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  heading: '#0F172A',

  // Buttons
  primaryBg: '#4F46E5',
  primaryText: '#FFFFFF',
  primaryHover: '#4338CA',
  secondaryBg: '#F8FAFC',
  secondaryBorder: '#E2E8F0',
  secondaryText: '#334155',
  secondaryHoverBg: '#F1F5F9',

  // Step / mode selection cards
  cardBg: '#FFFFFF',
  cardBorderSubtle: 'rgba(226, 232, 240, 0.5)',
  cardBorderLavender: 'rgba(79, 70, 229, 0.3)',
  cardBorder: '#E2E8F0',
  cardBorderHoverPrimary: '#4F46E5',
  cardBorderHover: 'rgba(79, 70, 229, 0.4)',
  cardSelectedBorder: '#4F46E5',
  cardSelectedRing: 'rgba(79, 70, 229, 0.15)',
  cardLinkAccent: '#0EA5E9',
  cardSingleAccent: '#4F46E5',
  cardBulkAccent: '#10B981',
  cardTransparentBorder: 'transparent',

  // Icon circles
  cardIconBgPurple: 'rgba(79, 70, 229, 0.1)',
  cardLinkIconBg: 'rgba(14, 165, 233, 0.12)',
  cardLinkIconBgSubtle: 'rgba(14, 165, 233, 0.08)',
  cardLinkBorderHover: 'rgba(14, 165, 233, 0.5)',
  cardSingleIconBg: 'rgba(79, 70, 229, 0.1)',
  cardSingleIconBgSubtle: 'rgba(79, 70, 229, 0.06)',
  cardSingleBorderHover: 'rgba(79, 70, 229, 0.5)',
  cardBulkIconBg: 'rgba(16, 185, 129, 0.1)',
  cardBulkIconBgSubtle: 'rgba(16, 185, 129, 0.06)',
  cardBulkBorderHover: 'rgba(16, 185, 129, 0.5)',

  // Channel-specific colors
  emailAccent: '#4F46E5',
  emailBg: 'rgba(79, 70, 229, 0.08)',
  emailBgHover: 'rgba(79, 70, 229, 0.12)',
  smsAccent: '#0EA5E9',
  smsBg: 'rgba(14, 165, 233, 0.08)',
  smsBgHover: 'rgba(14, 165, 233, 0.12)',
  whatsappAccent: '#25D366',
  whatsappBg: 'rgba(37, 211, 102, 0.08)',
  whatsappBgHover: 'rgba(37, 211, 102, 0.12)',

  // Indicators
  stepActive: '#4F46E5',
  stepComplete: '#10B981',
  stepInactive: '#CBD5E1',
  focusRing: '#4F46E5',

  // Header / footer
  headerBorder: '#F1F5F9',
  footerBorder: '#F1F5F9',
  closeHoverBg: '#F1F5F9',
};

const inviteFlowModalTheme = {
  INVITE_MODAL_SIZING,
  INVITE_MODAL_BREAKPOINTS,
  INVITE_PALETTE,
  INVITE_MODAL_COLORS,
};

export default inviteFlowModalTheme;
